import { FilterMenu } from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { PERIOD_PRESETS, type PeriodPreset } from '../report-period.ts';
import { usePageContext } from '../usePageContext.ts';
import type { OrderFilters } from './order-filters.ts';
import { useOrderFilterOptions } from './useOrderFilterOptions.ts';
import tools from '../ListTools.module.css';

export type OrderFilterBarProps = {
  api: TypedClient;
  shipping: ShippingClient;
  filters: OrderFilters;
  onChange: (filters: OrderFilters) => void;
};

const presetOf = (value: string | null): PeriodPreset | null =>
  PERIOD_PRESETS.find((preset) => preset === value) ?? null;

export const OrderFilterBar = ({ api, shipping, filters, onChange }: OrderFilterBarProps) => {
  const { t } = usePageContext();
  const options = useOrderFilterOptions(api, shipping);
  const clearLabelOf = (name: string) => t('orders.filter_remove', { name });
  return (
    <div className={tools['filters']} role="group" aria-label={t('orders.filters')}>
      <FilterMenu
        label={t('orders.filter_carrier')}
        options={options.carrier}
        value={filters.carrier}
        clearLabel={clearLabelOf(t('orders.filter_carrier'))}
        onChange={(carrier) => {
          onChange({ ...filters, carrier });
        }}
      />
      <FilterMenu
        label={t('orders.filter_city')}
        options={options.governorate}
        value={filters.governorate}
        clearLabel={clearLabelOf(t('orders.filter_city'))}
        onChange={(governorate) => {
          onChange({ ...filters, governorate });
        }}
      />
      {options.company.length > 1 ? (
        <FilterMenu
          label={t('companies.label')}
          options={options.company}
          value={filters.company}
          clearLabel={clearLabelOf(t('companies.label'))}
          onChange={(company) => {
            onChange({ ...filters, company });
          }}
        />
      ) : null}
      <FilterMenu
        label={t('orders.filter_date')}
        options={options.period}
        value={filters.period}
        clearLabel={clearLabelOf(t('orders.filter_date'))}
        onChange={(period) => {
          onChange({ ...filters, period: presetOf(period) });
        }}
      />
    </div>
  );
};
